import React, { useState, useEffect } from 'react';
import Lottie from 'lottie-react';
import { Target, Users, Zap, Award, Linkedin, Rocket } from 'lucide-react';
import { useData } from '../context/DataContext';
const aboutImage = '/images/patoyazılıminfografik2 (4).svg';

import SEO from '../components/SEO';
import FadeIn from '../components/FadeIn';
import { useTranslation } from 'react-i18next';

const About: React.FC = () => {
  const { teamMembers } = useData();
  const { t } = useTranslation();
  const [lottieData, setLottieData] = useState<any>(null);

  useEffect(() => {
    fetch('https://lottie.host/25508a01-866c-4a96-a04e-fa740eea66f7/bAS5Vxwibg.json')
      .then(response => response.json())
      .then(data => setLottieData(data))
      .catch(error => console.error('Error loading Lottie animation:', error));
  }, []);

  return (
    <div className="w-full bg-brand-light">
      <SEO
        pageCode="about"
        title="Hakkımızda | Pato Yazılım"
        description="Pato Yazılım olarak İstanbul Şişli'de, yenilikçi web tasarım ve yapay zeka çözümleri ile işletmenizi dijitale taşıyoruz."
        link="/hakkimizda"
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-brand-dark overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-brand-purple/20 blur-3xl rounded-full translate-x-1/2"></div>
        <FadeIn className="container mx-auto px-4 md:px-8 relative z-10 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {t('about.whoAreWe')}
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            {t('about.whoAreWeDesc')}
          </p>
        </FadeIn>
      </section>

      {/* Innovation Section */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <FadeIn className="w-full lg:w-1/2 order-2 lg:order-1" direction="left">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-blue/10 rounded-full text-brand-blue font-bold text-sm mb-6">
                <Rocket size={16} />
                <span>{t('about.innovationTag')}</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-brand-dark mb-6 leading-tight">
                {t('about.innovationTitle').split('\n')[0]} <br />
                <span className="text-brand-blue">{t('about.innovationTitle').split('\n')[1]?.trim()}</span>
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-6">
                {t('about.innovationDesc1')}
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                {t('about.innovationDesc2')}
              </p>
            </FadeIn>
            <FadeIn className="w-full lg:w-1/2 order-1 lg:order-2" direction="right" delay={0.2}>
              <div className="relative">
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-brand-purple/10 rounded-full blur-2xl"></div>
                <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-brand-blue/10 rounded-full blur-2xl"></div>
                {lottieData && (
                  <Lottie
                    animationData={lottieData}
                    loop={true}
                    className="w-full max-w-lg mx-auto"
                  />
                )}
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-24">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <FadeIn className="relative" direction="left">
              <img
                src={aboutImage}
                alt="Pato Yazılım Ofis Ortamı"
                className="rounded-2xl shadow-2xl z-10 relative w-full outline-none border-none ring-0"
              />
            </FadeIn>
            <FadeIn direction="right" delay={0.2}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-brand-blue/10 rounded-lg flex items-center justify-center text-brand-blue">
                  <Target size={24} />
                </div>
                <h2 className="text-3xl font-bold text-brand-dark">{t('about.missionVision')}</h2>
              </div>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {t('about.missionDesc1')}
              </p>
              <p className="text-gray-700 mb-8 leading-relaxed">
                {t('about.missionDesc2')}
              </p>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                  <h3 className="text-4xl font-bold text-brand-blue mb-2">5+</h3>
                  <p className="text-gray-500 font-medium">{t('about.yearsExperience')}</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                  <h3 className="text-4xl font-bold text-brand-blue mb-2">30+</h3>
                  <p className="text-gray-500 font-medium">{t('about.completedProjects')}</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Stats / Features */}
      <section className="py-20 bg-brand-blue/5 border-y border-brand-blue/10">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <FadeIn delay={0.1}>
              <div className="bg-white p-8 rounded-2xl shadow-md hover:-translate-y-1 transition-transform h-full">
                <div className="w-16 h-16 bg-blue-100 text-brand-blue rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users size={32} />
                </div>
                <h3 className="text-xl font-bold text-brand-dark mb-3">{t('about.expertTeam')}</h3>
                <p className="text-gray-700">
                  {t('about.expertTeamDesc')}
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="bg-white p-8 rounded-2xl shadow-md hover:-translate-y-1 transition-transform h-full">
                <div className="w-16 h-16 bg-purple-100 text-brand-purple rounded-full flex items-center justify-center mx-auto mb-6">
                  <Zap size={32} />
                </div>
                <h3 className="text-xl font-bold text-brand-dark mb-3">{t('about.fastSolutions')}</h3>
                <p className="text-gray-700">
                  {t('about.fastSolutionsDesc')}
                </p>
              </div>
            </FadeIn>
            <FadeIn delay={0.3}>
              <div className="bg-white p-8 rounded-2xl shadow-md hover:-translate-y-1 transition-transform h-full">
                <div className="w-16 h-16 bg-blue-100 text-brand-blue rounded-full flex items-center justify-center mx-auto mb-6">
                  <Award size={32} />
                </div>
                <h3 className="text-xl font-bold text-brand-dark mb-3">{t('about.qualityGuarantee')}</h3>
                <p className="text-gray-700">
                  {t('about.qualityGuaranteeDesc')}
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>



      {/* Team Section */}
      <section className="py-24">
        <div className="container mx-auto px-4 md:px-8">
          <FadeIn className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-purple-400 inline-block mb-4">
              {t('about.teamTitle')}
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto mt-6">
              {t('about.teamDesc')}
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {teamMembers.map((member, idx) => (
              <FadeIn key={member.id} delay={idx * 0.1}>
                <div className="group relative overflow-hidden rounded-2xl shadow-2xl h-[400px]">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
                  />
                  {/* Default visible info (when not hovering) */}
                  <div className="absolute bottom-0 left-0 w-full p-6 bg-gradient-to-t from-brand-dark/90 to-transparent group-hover:opacity-0 transition-opacity duration-300">
                    <h3 className="text-xl font-bold text-white">{member.name}</h3>
                    <p className="text-gray-300 text-sm">{member.role}</p>
                  </div>

                  <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/95 via-brand-dark/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-8 pointer-events-none group-hover:pointer-events-auto">
                    <h3 className="text-2xl font-bold text-white mb-1">{member.name}</h3>
                    <p className="text-brand-blue font-medium mb-4">{member.role}</p>

                    {member.linkedin && (
                      <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 bg-brand-blue hover:bg-blue-600 text-white px-6 py-3 rounded-lg transition-all shadow-lg hover:shadow-brand-blue/40 w-fit pointer-events-auto"
                      >
                        <Linkedin size={20} />
                        <span className="font-bold">LinkedIn</span>
                      </a>
                    )}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;